import React from 'react';
import { ProductHero } from '../components/sections/ProductHero';
import { ProductSection } from '../components/sections/ProductSection';
import { DeveloperCallout } from '../components/sections/DeveloperCallout';
import { FinalCTA } from '../components/sections/FinalCTA';
import { ModeProps } from '../types';
import { Layers, Globe, Wallet, RefreshCw, ShieldCheck, Coins, Droplets, Code2 } from 'lucide-react';
export function ProductsPage({
  mode
}: ModeProps) {
  // Only render for business mode, though structure supports both
  if (mode !== 'business') return null;
  return <main className="flex-grow" data-id="element-2396">
      <ProductHero data-id="element-2397" />

      {/* Product Suite Overview Section */}
      <section className="py-12 bg-white" data-id="element-2398">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2399">
          <div className="text-center max-w-3xl mx-auto" data-id="element-2400">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-2401">
              Product Suite Overview
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2402">
              Explore our modular infrastructure stack designed for modern money
              movement.
            </p>
          </div>
        </div>
      </section>

      <ProductSection id="orchestrate" title="Orchestrate" icon={Layers} overview="Orchestrate is Obseq's intelligent control layer that coordinates payments, FX, wallets, liquidity, and compliance across fiat and stablecoin rails." processSteps={['Transaction intent received', 'Rules engine evaluates routing options', 'Compliance checks applied', 'Optimal rail selected', 'Execution + monitoring']} useCases={[{
      title: 'Embedded finance platforms',
      link: '#'
    }, {
      title: 'Marketplace payouts',
      link: '#'
    }, {
      title: 'Global treasury orchestration',
      link: '#'
    }, {
      title: 'Multi-region fintech expansion',
      link: '#'
    }]} reverse={false} data-id="element-2403" />

      <ProductSection id="payments" title="Payments" icon={Globe} overview="Send and receive money globally using local rails, international transfers, and stablecoin settlement — through one API." processSteps={['Initiate collection or payout', 'Select local / global rail', 'Apply FX if required', 'Settle to bank or wallet', 'Confirm via webhook']} useCases={[{
      title: 'Supplier & vendor payments',
      link: '#'
    }, {
      title: 'Payroll disbursements',
      link: '#'
    }, {
      title: 'Marketplace settlements',
      link: '#'
    }, {
      title: 'Cross-border collections',
      link: '#'
    }]} reverse={true} data-id="element-2404" />

      <ProductSection id="wallets" title="Wallets" icon={Wallet} overview="Business-grade wallets for holding, segregating, and managing fiat and stablecoin balances at scale." processSteps={['Create wallet / sub-account', 'Fund via bank or stablecoin', 'Manage balances & permissions', 'Transfer internally or externally', 'Reconcile via ledger']} useCases={[{
      title: 'Treasury wallets',
      link: '#'
    }, {
      title: 'Platform sub-accounts',
      link: '#'
    }, {
      title: 'Client fund segregation',
      link: '#'
    }, {
      title: 'Stablecoin settlement accounts',
      link: '#'
    }]} reverse={false} data-id="element-2405" />

      <ProductSection id="exchange" title="Exchange" icon={RefreshCw} overview="Real-time FX conversion across fiat and stablecoins with transparent pricing and deep liquidity." processSteps={['Request quote', 'Lock rate', 'Execute conversion', 'Settle to destination', 'Confirm via API']} useCases={[{
      title: 'Cross-border FX',
      link: '#'
    }, {
      title: 'Treasury optimisation',
      link: '#'
    }, {
      title: 'Embedded FX',
      link: '#'
    }, {
      title: 'Stablecoin ↔ fiat conversion',
      link: '#'
    }]} reverse={true} data-id="element-2406" />

      <ProductSection id="compliance" title="Compliance" icon={ShieldCheck} overview="Embedded compliance infrastructure covering onboarding, screening, monitoring, and reporting across jurisdictions." processSteps={['Identity verification', 'Screening & risk assessment', 'Transaction monitoring', 'Regulatory reporting', 'Ongoing due diligence']} useCases={[{
      title: 'Regulated fintech onboarding',
      link: '#'
    }, {
      title: 'Marketplace compliance',
      link: '#'
    }, {
      title: 'Cross-border risk controls',
      link: '#'
    }, {
      title: 'AML/KYC automation',
      link: '#'
    }]} reverse={false} data-id="element-2407" />

      <ProductSection id="tokenize" title="Tokenize" icon={Coins} overview="Create and manage tokenized representations of money and programmable settlement flows." processSteps={['Define token parameters', 'Mint tokenized balance', 'Program settlement rules', 'Execute on-chain', 'Reconcile & report']} useCases={[{
      title: 'Tokenized deposits',
      link: '#'
    }, {
      title: 'Conditional payments',
      link: '#'
    }, {
      title: 'Escrow flows',
      link: '#'
    }, {
      title: 'Smart treasury',
      link: '#'
    }]} reverse={true} data-id="element-2408" />

      <ProductSection id="liquidity" title="Liquidity" icon={Droplets} overview="Access and route liquidity across banks, market makers, and on-chain sources." processSteps={['Liquidity request', 'Source evaluation', 'Route optimization', 'Execute settlement', 'Reconcile positions']} useCases={[{
      title: 'FX depth management',
      link: '#'
    }, {
      title: 'Stablecoin liquidity',
      link: '#'
    }, {
      title: 'High-volume settlement',
      link: '#'
    }, {
      title: 'Multi-source routing',
      link: '#'
    }]} reverse={false} data-id="element-2409" />

      <ProductSection id="connect" title="Connect" icon={Code2} overview="APIs, SDKs, and webhooks designed for fast, secure, production-grade integration." processSteps={['API key generation', 'Sandbox testing', 'Integration build', 'Production deployment', 'Monitoring & support']} useCases={[{
      title: 'Fintech product integration',
      link: '#'
    }, {
      title: 'Embedded finance',
      link: '#'
    }, {
      title: 'Internal tooling',
      link: '#'
    }, {
      title: 'Custom workflows',
      link: '#'
    }]} reverse={true} data-id="element-2410" />

      <DeveloperCallout data-id="element-2411" />
      <FinalCTA mode={mode} data-id="element-2412" />
    </main>;
}